/* eslint-disable @nx/enforce-module-boundaries */

import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Money } from '@principle-theorem/accounting';
import {
  OrganisationService,
  AccountingFunctionsService,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  DialogPresets,
} from '@principle-theorem/ng-shared';
import {
  balanceOverpaymentWithDeposit,
  Transaction,
} from '@principle-theorem/principle-core';
import {
  FundingRequestStatus,
  IHealthcareClaim,
  IHealthcareClaimItem,
  IInvoice,
  IMohClaimFundingRequest,
  IMohExtendedData,
  ITransaction,
  TransactionAction,
  TransactionProvider,
  TransactionStatus,
  TransactionType,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  snapshotDefined,
  toMoment,
  toTimestamp,
  WithRef,
} from '@principle-theorem/shared';
import { omit } from 'lodash';
import {
  IMohApproveClaimDialogData,
  MohApproveClaimDialogComponent,
} from '../components/moh/moh-approve-claim-dialog/moh-approve-claim-dialog.component';

export interface IMohClaimApprovalContext {
  invoice: WithRef<IInvoice>;
  transaction: WithRef<ITransaction<IMohExtendedData>>;
  claimRequest: WithRef<IMohClaimFundingRequest>;
  claim: IHealthcareClaim;
}

@Injectable()
export class MohClaimApprovalService {
  private _dialog = inject(MatDialog);
  private _basicDialog = inject(BasicDialogService);
  private _accountFunctions = inject(AccountingFunctionsService);
  private _organisation = inject(OrganisationService);
  private _snackBar = inject(MatSnackBar);

  /**
   * Resolves `false` when the approve dialog is dismissed without approving the
   * claim, or when every item of a non-zero claim was rejected: that claim is
   * left for Mark as Declined rather than settled as a $0 payment.
   */
  async processApproval(context: IMohClaimApprovalContext): Promise<boolean> {
    const extendedData = await this._dialog
      .open<
        MohApproveClaimDialogComponent,
        IMohApproveClaimDialogData,
        IMohExtendedData
      >(
        MohApproveClaimDialogComponent,
        DialogPresets.medium({
          data: {
            transaction: context.transaction,
            invoice: context.invoice,
            claim: context.claim,
          },
        })
      )
      .afterClosed()
      .toPromise();
    if (!extendedData || extendedData.amountPayable === undefined) {
      return false;
    }
    const isFullyRejected =
      extendedData.amountPayable === 0 && extendedData.amountClaimed > 0;
    if (isFullyRejected) {
      this._snackBar.open(
        'Every item on this claim was rejected, so it was not approved. Use Mark as Declined instead.'
      );
      return false;
    }

    const isUnderpayment =
      extendedData.amountPayable < extendedData.amountClaimed;
    const isOverpayment =
      extendedData.amountPayable > extendedData.amountClaimed;

    if (isUnderpayment) {
      await this._processUnderpayment(context, extendedData);
      return true;
    }
    if (isOverpayment) {
      await this._processOverpayment(context, extendedData);
      return true;
    }

    await this._approveTransaction(context, extendedData);
    return true;
  }

  private async _processUnderpayment(
    context: IMohClaimApprovalContext,
    extendedData: IMohExtendedData
  ): Promise<void> {
    await this._approveTransaction(context, extendedData);

    const remainingAmount = Money.amount(
      extendedData.amountClaimed - (extendedData.amountPayable ?? 0)
    );
    const prompt = `This MOH claim has resulted in an underpayment of $${remainingAmount}. Would you like to discount the remaining amount, or leave the amount outstanding?`;
    const shouldDiscount = await this._basicDialog.confirm({
      title: 'MOH Underpayment',
      prompt,
      submitLabel: 'Discount Remainder',
      cancelLabel: 'Leave Outstanding',
    });

    if (shouldDiscount) {
      const discountTransaction = Transaction.init({
        ...omit(context.transaction, 'ref'),
        ...Transaction.internalReference(TransactionProvider.Discount),
        amount: remainingAmount,
        type: TransactionType.Incoming,
        status: TransactionStatus.Complete,
        description: `MOH Claim Underpayment Discount`,
      });
      await this._accountFunctions.addTransactionToInvoice(
        context.invoice,
        discountTransaction,
        TransactionAction.Add
      );
    }
  }

  private async _processOverpayment(
    context: IMohClaimApprovalContext,
    extendedData: IMohExtendedData
  ): Promise<void> {
    await this._approveTransaction(context, extendedData);

    const shouldCreateAccountCredit = await this._basicDialog.confirm({
      title: 'MOH Overpayment',
      prompt:
        'This MOH claim has resulted in an overpayment. The difference can be converted to Account Credit, or left as an overpaid amount on the invoice.',
      submitLabel: 'Create Account Credit',
      cancelLabel: 'Leave as Overpayment',
    });

    if (shouldCreateAccountCredit) {
      const staffer = await snapshotDefined(this._organisation.staffer$);
      const taxRate = await snapshotDefined(this._organisation.taxRate$);
      await balanceOverpaymentWithDeposit(context.invoice, staffer, taxRate);
    }
  }

  private async _approveTransaction(
    { transaction, invoice, claim, claimRequest }: IMohClaimApprovalContext,
    extendedData: IMohExtendedData
  ): Promise<void> {
    if (extendedData.amountPayable === undefined) {
      return;
    }

    const approveTransaction = Transaction.init({
      ...transaction,
      amount: extendedData.amountPayable,
      status: TransactionStatus.Complete,
    });

    const approvedClaim = this._buildApprovedClaim(claim, extendedData);
    const transactionRef = await this._accountFunctions.addTransactionToInvoice(
      invoice,
      approveTransaction,
      TransactionAction.Approve,
      approvedClaim
    );

    if (transactionRef && extendedData.paymentDate) {
      await Firestore.patchDoc(transactionRef, {
        createdAt: toTimestamp(toMoment(extendedData.paymentDate)),
        extendedData,
      });
    }

    await Firestore.patchDoc(claimRequest.ref, {
      status: FundingRequestStatus.Paid,
    });
  }

  private _buildApprovedClaim(
    claim: IHealthcareClaim,
    extendedData: IMohExtendedData
  ): IHealthcareClaim {
    const approvedItems: IHealthcareClaimItem[] = extendedData.items
      .filter((item) => item.approved)
      .map((item) => ({
        label: `${item.treatment.description}: ${item.serviceCode.description}`,
        treatmentUid: item.treatment.uid,
        serviceCodeUid: item.serviceCode.uid,
        quantity: item.quantity,
        provider: claim.practitioner,
      }));

    return {
      ...claim,
      items: approvedItems,
    };
  }
}
